import {useState} from 'react'
import {useNavigate} from 'react-router-dom'

import Style from './style'

import MainHeader from 'widget/MainHeader'

import useGetFranchiseManagerDetail from 'entity/franchise/useGetFranchiseManagerDetail'
import usePutFranchiseManagerPassword from 'entity/franchise/usePutFranchiseManagerPassword'

import DTStyle from 'shared/style/detailTableStyle'
import TStyle from 'shared/style/tableStyle'
import timestampCalculator from 'shared/lib/timestampCalculator'
import TableLoading from 'shared/ui/TableLoading'
import useRoleCookie from 'shared/model/useRoleCookie'
import DefaultButton from 'shared/ui/DefaultButton'

import ChangePasswordModal from './ui/ChangePasswordModal'

import {
	ROLE_SUPER_ADMIN,
	ROLE_FRANCHISE_MANAGER,
	ROLE_FIELD_MANAGER,
	ROLE_TIMER_MANAGER,
	ROLE_COMPETITION_MANAGER,
	ROLE_STORE_MANAGER,
	convertRoleAsName,
} from 'shared/constant/lookup/role'

const FranchiseManagerDetail = () => {
	const navigate = useNavigate()

	const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)

	const [userRole] = useRoleCookie()

	const [franchiseManagerData, isLoading, refetchData] =
		useGetFranchiseManagerDetail()
	const [changePasswordRequest, isPasswordChanging] =
		usePutFranchiseManagerPassword(refetchData)

	const managerRole =
		franchiseManagerData?.data?.franchise_manager?.manager_role_flag
	const competitationInfo = franchiseManagerData?.data?.store_list?.find(
		(item) => item.manager_role_flag === ROLE_COMPETITION_MANAGER
	)
	const brandName = competitationInfo?.store_name
	const representativeContact = competitationInfo?.contact
	const representativeName = competitationInfo?.name

	const franchiseDataList = [
		{
			label: 'IDX',
			value: franchiseManagerData?.data?.franchise_manager?.franchise_idx,
		},
		{
			label: '대표 주소',
			value: franchiseManagerData?.data?.franchise_manager?.franchise_address,
		},
		{
			label: '이름',
			value: franchiseManagerData?.data?.franchise_manager?.franchise_name,
		},
		{
			label: '대표 연락처',
			value: franchiseManagerData?.data?.franchise_manager?.franchise_contact,
		},
		{
			label: '매장 이용권',
			value: franchiseManagerData?.data?.franchise_manager?.store_ticket_name,
		},
	]
	const franchiseManagerDataList = [
		{
			label: 'IDX',
			value: franchiseManagerData?.data?.franchise_manager?.idx,
		},
		{
			label: '상태',
			value: franchiseManagerData?.data?.franchise_manager?.is_approve
				? '승인'
				: '미승인',
		},
		{
			label: '이름',
			value: franchiseManagerData?.data?.franchise_manager?.name,
		},

		{
			label: '연락처',
			value: franchiseManagerData?.data?.franchise_manager?.contact,
		},
		...(userRole === ROLE_SUPER_ADMIN
			? [
					{
						label: 'ID',
						value: franchiseManagerData?.data?.franchise_manager?.id,
					},
					{
						label: '비밀번호',
						value: franchiseManagerData?.data?.franchise_manager?.password,
					},
					...(managerRole === ROLE_COMPETITION_MANAGER
						? [
								{
									label: '브랜드명',
									value: brandName,
								},
							]
						: []),
				]
			: []),
		{
			label: '가입 날짜',
			value: timestampCalculator(
				franchiseManagerData?.data?.franchise_manager?.created_at
			),
		},
	]

	const brandInformationList = [
		{
			label: '브랜드명',
			value: brandName,
			col: 2,
		},

		{
			label: '대표자명',
			value: representativeName,
		},
		{
			label: '대표 연락처',
			value: representativeContact,
		},
	]

	const logoImg =
		franchiseManagerData?.data?.franchise_manager?.franchise_logo_img

	const sizeList = ['short', 'long', 'long', 'expand', 'medium', 'long']
	const tableHeadList = [
		'IDX',
		'매니저 이름',
		'매니저 연락처',
		'매장 이름',
		'상태',
		'가입 날짜',
	]

	const getTitle = (role) => {
		if (role && convertRoleAsName[role]) {
			return `${convertRoleAsName[role]} 정보`
		}
		return '매니저 정보'
	}

	return (
		<Style.Wrapper>
			<MainHeader />

			<Style.AreaDiv>
				<Style.AreaHeader>
					<h2>{getTitle(managerRole)}</h2>
					{userRole === ROLE_SUPER_ADMIN && (
						<DefaultButton
							small
							clickEvent={() => setIsPasswordModalOpen(true)}
						>
							비밀번호 변경
						</DefaultButton>
					)}
				</Style.AreaHeader>
				<DTStyle.TableDiv>
					{franchiseManagerDataList?.map((elem, index) => (
						<DTStyle.TableElement key={index}>
							<h2>{elem?.label}</h2>
							<p>{elem?.value}</p>
						</DTStyle.TableElement>
					))}
				</DTStyle.TableDiv>
			</Style.AreaDiv>

			{managerRole === ROLE_FRANCHISE_MANAGER && (
				<>
					<Style.AreaDiv>
						<h2>프랜차이즈 정보</h2>
						<DTStyle.TableDiv>
							{franchiseDataList?.map((elem, index) => (
								<DTStyle.TableElement key={index}>
									<h2>{elem?.label}</h2>
									<p>{elem?.value}</p>
								</DTStyle.TableElement>
							))}
						</DTStyle.TableDiv>
					</Style.AreaDiv>
					{logoImg && (
						<Style.AreaDiv>
							<h2>로고 이미지</h2>
							<div>
								<Style.LogoImg src={logoImg} />
							</div>
						</Style.AreaDiv>
					)}

					<Style.AreaDiv>
						<h2>매장 목록</h2>

						<TableLoading isLoading={isLoading}>
							<TStyle.TableDiv>
								<TStyle.TableInner>
									<TStyle.TableRow $title>
										{tableHeadList.map((elem, index) => (
											<TStyle.Data key={index} $title $size={sizeList[index]}>
												{elem}
											</TStyle.Data>
										))}
									</TStyle.TableRow>
									{franchiseManagerData?.data?.store_list
										?.filter(
											(item) => item.manager_role_flag === ROLE_STORE_MANAGER
										)
										?.map((elem, index) => (
											<TStyle.TableRow
												key={index}
												$odd={index % 2 === 1}
												onClick={() =>
													navigate(`/manager/store/${elem?.idx}/detail`)
												}
											>
												<TStyle.Data $size={sizeList[0]}>
													{elem?.idx}
												</TStyle.Data>
												<TStyle.Data $size={sizeList[1]}>
													{elem?.name}
												</TStyle.Data>
												<TStyle.Data $size={sizeList[2]}>
													{elem?.contact}
												</TStyle.Data>
												<TStyle.Data $size={sizeList[3]}>
													{elem?.store_name}
												</TStyle.Data>
												<TStyle.Data $size={sizeList[4]}>
													{elem?.is_approve ? '승인' : '미승인'}
												</TStyle.Data>
												<TStyle.Data $size={sizeList[5]}>
													{timestampCalculator(elem?.created_at)}
												</TStyle.Data>
											</TStyle.TableRow>
										))}
								</TStyle.TableInner>
							</TStyle.TableDiv>
						</TableLoading>
					</Style.AreaDiv>
				</>
			)}

			{(managerRole === ROLE_FIELD_MANAGER ||
				managerRole === ROLE_TIMER_MANAGER) && (
				<>
					<Style.AreaDiv>
						<h2>브랜드 정보</h2>
						<DTStyle.TableDiv>
							{brandInformationList?.map((elem, index) => (
								<DTStyle.TableElement key={index} $fullWidth={elem?.col === 2}>
									<h2>{elem?.label}</h2>
									<p>{elem?.value}</p>
								</DTStyle.TableElement>
							))}
						</DTStyle.TableDiv>
					</Style.AreaDiv>
				</>
			)}

			{isPasswordModalOpen && (
				<ChangePasswordModal
					isOpen={isPasswordModalOpen}
					onClose={() => setIsPasswordModalOpen(false)}
					onSubmit={(newPassword) => changePasswordRequest(newPassword)}
					isLoading={isPasswordChanging}
				/>
			)}
		</Style.Wrapper>
	)
}

export default FranchiseManagerDetail
